import { useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import metadata from './CriminalDamage.metadata.json5';
import styles from './CriminalDamage.module.scss';

import { IconButton, InputField, DropdownSelectField } from '@jutro/legacy/components';

function FNOLCriminalDamage(props) {
    const {
        criminalDamageData: claimVM,
        updateWizardData, showErrors, onValidate
    } = props;
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);

    const { registerComponentValidation } = useValidation('FNOLCriminalDamage');

    useEffect(
        () => {
            const officialReport = _.get(claimVM, 'officialReport_TFPA.value', []);
            const isPoliceReportFiled = _.get(claimVM, 'lobs.homeowners.propTheftVanDamageDTO_TFPA.isPoliceReportFiled.value', false);
            if (isPoliceReportFiled && officialReport?.length <= 0) {
               addNewReport();
            } else if (!isPoliceReportFiled) {
                _.set(claimVM, 'officialReport_TFPA.value', []);
                updateWizardData(claimVM);
            }
        },
        [_.get(claimVM, 'lobs.homeowners.propTheftVanDamageDTO_TFPA.isPoliceReportFiled.value', false)]
    );

    const isClaimStatus = useCallback(() => {
        return (
            ['burglary', 'vandalism'].includes(_.get(claimVM.value, 'lossCause'))
        );
    }, [claimVM]);

    useEffect(() => {
        registerComponentValidation(isClaimStatus);
    }, [registerComponentValidation, isClaimStatus]);

    const onReportDelete = (indexToDelete) => {
        claimVM.officialReport_TFPA.value?.splice(indexToDelete, 1);
        updateWizardData(claimVM);
    };

    const getDeleteBtn = useCallback((item, index) => {
        if (index > 0) {
            return (
                <IconButton
                    icon="mi-delete"
                    id={`delete${index}`}
                    size="large"
                    type="tertiary"
                    tooltip={{
                        placement: 'top',
                        text: 'Delete'
                    }}
                    onClick={() => onReportDelete(index)}
                />
            );
        }
        return undefined;
    }, []);

    const writeValue = useCallback((value, path, index) => {
        _.set(claimVM, `officialReport_TFPA.children[${index}].${path}.value`, value);
        updateWizardData(claimVM);
    }, [claimVM]);

    const getReportTypeRequiredIcon = useCallback(() => {
        return (
            <div>
                <span className={styles.requiredSymbol}>*</span>
            </div>
        );
    });

    const getReportTypeHeader = useCallback(() => {
        return (
            <div>
                <span>Type of Report</span>
                <span className={styles.requiredSymbol}>&nbsp;&nbsp;*</span>
            </div>
        );
    });

    const getReportTypeAvailableValues = () => {
        return claimVM.officialReport_TFPA.children[0].reportType.aspects.availableValues.map((reportType) => {
            return {
                code: reportType.code,
                name: translator({
                    id: `typekey.OfficialType.${reportType.code}`,
                }),
            };
        });
    };

    const getReportType = useCallback((item, index, property) => {
        return (
            <DropdownSelectField
                id={`reportTypeField${index}`}
                value={item.reportType}
                availableValues={getReportTypeAvailableValues()}
                layout="full-width"
                required
                showRequired
                showErrors={showErrors}
                placeholder=""
                onValueChange={(value) => writeValue(value, 'reportType', index)}
            />
        );
    }, [claimVM, showErrors]);

    const getReportName = useCallback((item, index) => {
        return (
            <InputField
                id={`reportNameField${index}`}
                type="field"
                path={`${claimVM}.officialReport_TFPA.children[${index}].name`}
                onValueChange={(value) => writeValue(value, 'name', index)}
                value={item.name}
            />
        );
    }, [claimVM]);

    const getReportNumber = useCallback((item, index) => {
        return (
            <InputField
                id={`reportNumberField${index}`}
                type="field"
                path={`officialReport_TFPA.children[${index}].reportNumber`}
                onValueChange={(value) => writeValue(value, 'reportNumber', index)}
                value={item.reportNumber}
            />
        );
    }, []);

    const addNewReport = useCallback(() => {
        const officialReportDTO = viewModelService.create({}, 'cc', 'org.tfpa.edge.capabilities.claim.fnoltfpa.OfficialDTO_TFPA');
        if (!claimVM.officialReport_TFPA?.value) {
            _.set(claimVM, 'officialReport_TFPA.value', []);
        }
        claimVM.officialReport_TFPA.value.push(officialReportDTO.value);
        updateWizardData(claimVM);
    }, [claimVM]);

    const overrides = {
        '@field': {
            showOptional: false,
            phoneWide: {
                labelPosition: 'top',
            },
        },
        addReportTable: {
            data: _.get(claimVM, 'officialReport_TFPA.value', []),
        },
        addReportContainer: {
            visible: _.get(claimVM, 'lobs.homeowners.propTheftVanDamageDTO_TFPA.isPoliceReportFiled.value', false)
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getReportTypeHeader: getReportTypeHeader,
            getReportType: getReportType,
            getReportName: getReportName,
            getReportNumber: getReportNumber,
            getDeleteBtn: getDeleteBtn,
            addNewReport: addNewReport,
            getReportTypeRequiredIcon: getReportTypeRequiredIcon
        }
    };
    return (
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showErrors}
            />
    );
}

FNOLCriminalDamage.propTypes = wizardProps;
export default withRouter(FNOLCriminalDamage);
